import { useRef } from 'react';

export function useDetectChanges() {
  const changesRef = useRef<Changes>({});

  const makeChanges = ({
    key,
    prevValue,
    newValue,
  }: {
    key: string;
    prevValue: boolean | string | null;
    newValue: boolean | string | null;
  }) => {
    const foundChange = changesRef.current[key];

    if (foundChange) {
      foundChange.newValue = newValue;
    } else {
      changesRef.current[key] = {
        originalValue: prevValue,
        newValue,
      };
    }
  };

  return {
    updateRating: ({
      ratableItemId,
      prevValue,
      newValue,
      ratableItemType,
    }: UpdateRatingArgs) => {
      makeChanges({
        key: generateRatingChangeKey({ ratableItemId, ratableItemType }),
        prevValue,
        newValue,
      });
    },

    updatePerceivedValue: ({
      question,
      prevValue,
      newValue,
    }: UpdatePerceivedValueArgs) => {
      makeChanges({
        key: generatePerceivedValueChangeKey({ question }),
        prevValue,
        newValue,
      });
    },

    updateFoodFeedback: ({
      foodFeedbackId,
      prevValue,
      newValue,
    }: UpdateFoodFeedbackArgs) => {
      makeChanges({
        key: generateFoodFeedbackChangeKey({ foodFeedbackId }),
        prevValue,
        newValue,
      });
    },

    updateFoodFeedbackSecondary: ({
      foodFeedbackId,
      foodFeedbackSecondaryId,
      foodFeedbackSecondaryType,
      prevValue,
      newValue,
    }: UpdateFoodFeedbackTagArgs) => {
      makeChanges({
        key: generateFoodFeedbackSecondaryChangeKey({
          foodFeedbackId,
          foodFeedbackSecondaryId,
          foodFeedbackSecondaryType,
        }),
        prevValue,
        newValue,
      });
    },

    hasChanges: () => {
      for (const key in changesRef.current) {
        const change = changesRef.current[key];
        if (change.originalValue !== change.newValue) {
          return true;
        }
      }

      return false;
    },

    clearChanges: () => {
      changesRef.current = {};
    },
  };
}

function generateRatingChangeKey({
  ratableItemType,
  ratableItemId,
}: {
  ratableItemType: 'bundle' | 'pairing' | 'product';
  ratableItemId: number;
}) {
  return `rating-${ratableItemType}-${ratableItemId}`;
}

function generateFoodFeedbackChangeKey({
  foodFeedbackId,
}: {
  foodFeedbackId: number;
}) {
  return `foodFeedback-${foodFeedbackId}`;
}

function generateFoodFeedbackSecondaryChangeKey({
  foodFeedbackId,
  foodFeedbackSecondaryId,
  foodFeedbackSecondaryType,
}: {
  foodFeedbackId: number;
  foodFeedbackSecondaryId: number;
  foodFeedbackSecondaryType: 'tag' | 'ingredient';
}) {
  return `${generateFoodFeedbackChangeKey({
    foodFeedbackId,
  })}-foodFeedbackSecondary-${foodFeedbackSecondaryType}-${foodFeedbackSecondaryId}`;
}

function generatePerceivedValueChangeKey({ question }: { question: number }) {
  return `perceivedValue-${question}`;
}

type UpdatePerceivedValueArgs = {
  question: number;
  prevValue: string | null;
  newValue: string | null;
};

type UpdateRatingArgs = {
  ratableItemId: number;
  prevValue: boolean | null;
  newValue: boolean | null;
  ratableItemType: 'bundle' | 'pairing' | 'product';
};

type UpdateFoodFeedbackArgs = {
  foodFeedbackId: number;
  prevValue: boolean;
  newValue: boolean;
};

type UpdateFoodFeedbackTagArgs = {
  foodFeedbackId: number;
  foodFeedbackSecondaryId: number;
  foodFeedbackSecondaryType: 'tag' | 'ingredient';
  prevValue: boolean;
  newValue: boolean;
};

type Changes = {
  [key: string]: {
    originalValue: boolean | string | null;
    newValue: boolean | string | null;
  };
};
